import { ReactNode } from "react";
import { motion } from "framer-motion";
import "./SignupUI.scss";

interface SignupStepProps {
  /** 1-based position in the flow; omit on steps outside the numbered path. */
  step?: number;
  totalSteps?: number;
  title: string;
  /** Kept so steps can preserve their existing test hooks on the heading. */
  titleDataCy?: string;
  subtitle?: ReactNode;
  children: ReactNode;
  /** Buttons. Rendered full width at the foot of the card. */
  actions?: ReactNode;
  /** Small print under the card, outside the panel. */
  below?: ReactNode;
  id?: string;
}

const TOTAL_STEPS = 8;

/**
 * One signup step.
 *
 * Replaces the `unAuth-card` / `signup-header` / `form-wrapper` markup every
 * step repeated. The progress bar is presentational only — it reads the step
 * number it is given and drives nothing.
 */
export const SignupStep = ({
  step,
  totalSteps = TOTAL_STEPS,
  title,
  titleDataCy,
  subtitle,
  children,
  actions,
  below,
  id,
}: SignupStepProps) => (
  <div className="suWrap">
    {/* A `div`, not a `section`. The card has no accessible name, so `section`
        bought nothing, and every `*-container` id in the suite is asserted as
        `div#…` — the step cards carry those ids. */}
    <motion.div
      className="suCard"
      id={id}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
    >
      {step !== undefined && (
        <div className="suProgress">
          <div className="suProgress__track">
            {Array.from({ length: totalSteps }).map((_, index) => (
              <span key={index} className={"suProgress__seg" + (index < step ? " suProgress__seg--on" : "")} />
            ))}
          </div>
          <span className="suProgress__label">
            {step} / {totalSteps}
          </span>
        </div>
      )}

      <header className="suHead">
        <h1 className="suHead__title" data-cy={titleDataCy}>
          {title}
        </h1>
        {subtitle && <p className="suHead__subtitle">{subtitle}</p>}
      </header>

      <div className="suBody">{children}</div>

      {actions && <div className="suActions">{actions}</div>}
    </motion.div>

    {below && <div className="suBelow">{below}</div>}
  </div>
);
